import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { resetShimmer } from '@theme/utilities';

window.scpProps = window.scpProps || { '__preTaxPrice': 0 };

// SHIP +
const insuranceProductID = '47924487160058';

// Serializes all cart-insurance operations so overlapping triggers
// (DOMContentLoaded, cart-drawer:opened, cart:update, manual toggle)
// never race each other and cause a double add/remove.
let insuranceLock = null;

// -------------------- Cart state helpers --------------------

async function getCart() {
    const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
    });
    return res.json();
}

async function findInsurance() {
    const cart = await getCart();
    const item = cart.items.find((i) => Number(i.id) === Number(insuranceProductID));
    return { item, cart };
}

// -------------------- Shimmer helpers --------------------

function shimmer(on = true) {
    if (on) {
        document
            .querySelectorAll(
                'text-component[ref="cartTotal"], .cart__total text-component'
            )
            .forEach((t) => t?.shimmer?.());
        return;
    }

    resetShimmer(document.body);
}

async function refreshOpenCartDrawer() {
    const openDrawer = document.querySelector('cart-drawer-component dialog[open]')?.closest('cart-drawer-component');
    if (!openDrawer) return;

    const sectionId = openDrawer.querySelector('cart-items-component')?.dataset.sectionId;
    if (!sectionId) return;

    const html = await fetch(`/?section_id=${encodeURIComponent(sectionId)}`, {
        credentials: 'same-origin',
        cache: 'no-store',
    }).then((res) => res.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextInner = doc.querySelector('.cart-drawer__inner');
    const inner = openDrawer.querySelector('.cart-drawer__inner');
    if (nextInner && inner) {
        const imported = document.importNode(nextInner, true);
        inner.replaceWith(imported);
        customElements.upgrade(imported);
    }
    document.querySelectorAll('cart-items-component.cart-items-disabled').forEach((el) => {
        el.classList.remove('cart-items-disabled');
    });
}

// -------------------- Add / Remove --------------------

async function addInsurance() {
    shimmer(true);
    await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
            id: insuranceProductID,
            quantity: 1,
            properties: window.scpProps,
        }),
    });
    shimmer(false);
    await refreshOpenCartDrawer();
    const cart = await getCart();
    document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-insurance', { source: 'cart-insurance' })
    );
}

async function removeInsurance(item) {
    if (!item?.key) return;
    shimmer(true);
    await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: item.key, quantity: 0 }),
    });
    shimmer(false);
    await refreshOpenCartDrawer();
    const cart = await getCart();
    document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-insurance', { source: 'cart-insurance' })
    );
}

// -------------------- Initial auto-add --------------------

async function _enforceInsurance() {
    if (document.documentElement.classList.contains('cart-drawer-loading')) return;

    const { item, cart } = await findInsurance();
    const denied = sessionStorage.getItem('shipPlusDenied') === 'true';
    const haveOtherItems = cart.items.some(
        (i) => Number(i.id) !== Number(insuranceProductID)
    );

    // remove if only insurance left
    if (item && !haveOtherItems) {
        await removeInsurance(item);
        updateToggle(false);
        return;
    }

    // add if missing and not denied
    if (!item && haveOtherItems && !denied) {
        await addInsurance();
        updateToggle(true);
        return;
    }

    updateToggle(!!item);
}

// Public entry point: if a check/add/remove cycle is already running,
// piggyback on it instead of starting a second, racing cycle.
function enforceInsurance() {
    if (insuranceLock) return insuranceLock;
    insuranceLock = _enforceInsurance().finally(() => {
        insuranceLock = null;
    });
    return insuranceLock;
}

// -------------------- Toggle handling --------------------

function updateToggle(state) {
    const toggle = document.querySelector('#insurance-toggle');
    if (toggle) toggle.checked = !!state;
}

async function handleInsurance(e) {
    const toggle = e.target;
    toggle.disabled = true;

    // Wait out any auto-enforcement that's already mid-flight so we
    // read/act on fresh cart state instead of racing it.
    if (insuranceLock) await insuranceLock;

    insuranceLock = (async () => {
        const { item } = await findInsurance();

        if (toggle.checked && !item) {
            await addInsurance();
            sessionStorage.setItem('shipPlusDenied', 'false');
        } else if (!toggle.checked && item) {
            await removeInsurance(item);
            sessionStorage.setItem('shipPlusDenied', 'true');
        }

        const refreshed = await findInsurance();
        toggle.checked = !!refreshed.item;
    })().finally(() => {
        insuranceLock = null;
    });

    await insuranceLock;
    toggle.disabled = false;
}

// -------------------- Event Listeners --------------------

document.body.addEventListener('change', function (e) {
    if (e.target && e.target.id === 'insurance-toggle') {
        handleInsurance(e);
    }
});

document.addEventListener('cart:update', (e) => {
    if (e?.detail?.data?.source === 'cart-insurance') return;
    enforceInsurance();
});

document.addEventListener('DOMContentLoaded', enforceInsurance);
document.addEventListener('cart-drawer:opened', enforceInsurance);